import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LANG_CODES,
  DEFAULT_LANG,
  LANG_STORAGE_KEY,
} from "@/i18n";

/**
 * Extracts the locale code from the first URL segment. Returns the default
 * language when the path has no locale prefix (e.g. `/about` → "en").
 */
function langFromPath(pathname) {
  const first = pathname.split("/").filter(Boolean)[0];
  return first && SUPPORTED_LANG_CODES.includes(first) ? first : DEFAULT_LANG;
}

/**
 * Reads the locale from the URL and syncs it into i18next + localStorage.
 * Renders no UI — mount it once near the top of the tree, inside <Router>.
 */
export function LanguageSync({ children }) {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const active = langFromPath(pathname);

  useEffect(() => {
    if (i18n.language !== active) {
      i18n.changeLanguage(active);
    }
    try {
      localStorage.setItem(LANG_STORAGE_KEY, active);
      // Also mirror the choice into a first-party cookie so server-side
      // proxies / CDNs can vary the response later without JS execution.
      document.cookie = `${LANG_STORAGE_KEY}=${active}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    } catch {
      /* ignore storage errors (private mode, etc.) */
    }
    // Reflect the active language on <html lang="…"> for a11y / SEO parsers.
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", active);
    }
  }, [active, i18n]);

  return children;
}

/**
 * Prefixes a path with the current locale (empty for English so URLs stay
 * canonical). Use for internal <Link to={localizedPath('/about')} /> calls.
 */
export function useLocalePath() {
  const { pathname } = useLocation();
  const active = langFromPath(pathname);
  return (path) => {
    const clean = path.startsWith("/") ? path : `/${path}`;
    if (active === DEFAULT_LANG) return clean;
    return `/${active}${clean === "/" ? "" : clean}`;
  };
}

/**
 * Rewrites the current URL to a new locale, preserving path + query + hash.
 * `/es/about?q=1#top` → `/ja/about?q=1#top` (or `/about?…` when switching to English).
 */
export function useSwitchLanguage() {
  const navigate = useNavigate();
  const location = useLocation();

  return (targetLang) => {
    if (!SUPPORTED_LANG_CODES.includes(targetLang)) return;
    let path = location.pathname;
    // Strip any existing lang prefix.
    const segments = path.split("/").filter(Boolean);
    if (segments.length && SUPPORTED_LANG_CODES.includes(segments[0])) {
      segments.shift();
    }
    const base = "/" + segments.join("/");
    const next = targetLang === DEFAULT_LANG ? base : `/${targetLang}${base === "/" ? "" : base}`;
    navigate(`${next}${location.search}${location.hash}`, { replace: false });
    try {
      localStorage.setItem(LANG_STORAGE_KEY, targetLang);
    } catch {
      /* ignore */
    }
  };
}
